import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, Loader2, CheckCircle, XCircle, Clock, MessageSquare, Eye, Download } from 'lucide-react';import toast from 'react-hot-toast';
import API from '../services/api';

function CampaignDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDetails();
  }, [id]);

  const loadDetails = async () => {
    setLoading(true);
    try {
      const res = await API.get(`/campaigns/${id}/details`);
      setData(res.data.data);
    } catch (error) {
      toast.error('Failed to load campaign details');
    } finally {
      setLoading(false);
    }
  };
  const handleExport = async () => {
  const toastId = toast.loading('Preparing export...');
  try {
    const res = await API.get(`/campaigns/${id}/export`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `campaign_${id}_export.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Exported!', { id: toastId });
  } catch (error) {
    toast.error('Failed to export campaign data', { id: toastId });
  }
};

  const statusBadge = (status) => {
    const map = {
      pending: { bg: '#f1f5f9', color: '#64748b', icon: <Clock size={12} />, label: 'Pending' },
      sent: { bg: '#dbeafe', color: '#1e40af', icon: <CheckCircle size={12} />, label: 'Sent' },
      delivered: { bg: '#dcfce7', color: '#166534', icon: <CheckCircle size={12} />, label: 'Delivered' },
      read: { bg: '#ede9fe', color: '#6d28d9', icon: <CheckCircle size={12} />, label: 'Read' },
      failed: { bg: '#fee2e2', color: '#991b1b', icon: <XCircle size={12} />, label: 'Failed' },
      draft: { bg: '#f1f5f9', color: '#64748b', icon: <Clock size={12} />, label: 'Draft' },
      processing: { bg: '#fef3c7', color: '#92400e', icon: <Loader2 size={12} />, label: 'Processing' },
      completed: { bg: '#dcfce7', color: '#166534', icon: <CheckCircle size={12} />, label: 'Completed' },
    };
    const s = map[status] || map.pending;
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '3px 10px', borderRadius: '20px', fontSize: '12px',
        fontWeight: '700', background: s.bg, color: s.color
      }}>
        {s.icon} {s.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="loading-container" style={{ padding: '60px 0' }}>
        <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#1e3a5f' }} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="dash-page">
        <div className="empty-state">
          <div className="empty-state-icon"><FileText size={56} color="#cbd5e1" /></div>
          <h3 className="empty-state-title">Campaign Not Found</h3>
          <button onClick={() => navigate('/bulk-message')} className="settings-btn-test" style={{ marginTop: '14px' }}>
            <ArrowLeft size={15} /> Back to Bulk Message
          </button>
        </div>
      </div>
    );
  }

  const { campaign, recipients } = data;

  return (
    <div className="dash-page">
<div className="dash-header">
  <div>
    <button onClick={() => navigate('/bulk-message')} className="settings-btn-test" style={{ marginBottom: '12px' }}>
      <ArrowLeft size={15} /> Back
    </button>
    <h1 className="dash-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <FileText size={26} color="#1e3a5f" /> {campaign.name || `Campaign #${campaign.id}`}
    </h1>
    <p className="dash-subtitle">
      Created on {new Date(campaign.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
    </p>
  </div>
  <button onClick={handleExport} className="settings-btn-save" style={{ height: 'fit-content' }}>
    <Download size={15} /> Export Campaign Data
  </button>
</div>

      {/* Summary */}
      <div className="dash-card mb-lg">
        <div className="settings-tab-header">
          <h2 className="settings-tab-title">Campaign Summary</h2>
          {statusBadge(campaign.status)}
        </div>
        <div className="settings-stats-grid">
          <div className="settings-stat-card settings-stat-card-primary">
            <div className="settings-stat-label">Total Recipients</div>
            <div className="settings-stat-value">{campaign.total_recipients}</div>
          </div>
          <div className="settings-stat-card settings-stat-card-green">
            <div className="settings-stat-label">Sent</div>
            <div className="settings-stat-value">{campaign.sent_count}</div>
          </div>
                    <div
            className="settings-stat-card settings-stat-card-pink"
            onClick={() => campaign.failed_count > 0 && navigate(`/bulk-message/campaign/${id}/failed`)}
            style={{ cursor: campaign.failed_count > 0 ? 'pointer' : 'default' }}
            title={campaign.failed_count > 0 ? 'Click to view and resend failed recipients' : ''}
          >
            <div className="settings-stat-label">Failed</div>
            <div className="settings-stat-value">{campaign.failed_count}</div>
            {campaign.failed_count > 0 && (
              <div style={{ fontSize: '11px', color: '#991b1b', marginTop: '4px', fontWeight: 600 }}>
                Click to view →
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recipients */}
      <div className="dash-card">
        <h2 className="settings-tab-title" style={{ marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MessageSquare size={18} /> Recipients ({recipients.length})
        </h2>

        {recipients.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><MessageSquare size={56} color="#cbd5e1" /></div>
            <h3 className="empty-state-title">No Recipients</h3>
          </div>
        ) : (
          <div className="table-container table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Error</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {recipients.map((r, i) => (
                  <tr key={r.contact_id || i}>
                    <td>{r.phone}</td>
                    <td>{statusBadge(r.status)}</td>
                    <td style={{ color: '#dc2626', fontSize: '12.5px' }}>{r.error_message || '—'}</td>
                    <td className="text-center">
                      {r.contact_id ? (
                        <button
                          onClick={() => navigate(`/bulk-message/campaign/${id}/recipient/${r.contact_id}`)}
                          className="btn btn-info btn-sm"
                        >
                          <Eye size={13} /> View
                        </button>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default CampaignDetails;